import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  BeakerIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PencilSquareIcon,
  HashtagIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PlusIcon,
  QrCodeIcon,
  Squares2X2Icon,
  UserGroupIcon,
  XMarkIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { apiDelete, apiGet, apiPatch, apiPost } from '../utils/api';
import { getApiUrl } from '../api/config';
import MapBaseComponent from '../components/maps/MapBaseComponent';
import { calculateCenter, RWANDA_DEFAULTS } from '../utils/geospatial';
import MapboxMiniMap from '../components/maps/MapboxMiniMap';
import TreeGridBuilderDrawer from '../components/traceability/TreeGridBuilderDrawer';

type TabKey = 'fields' | 'trees' | 'map' | 'lots';

const tilePerformanceOptions = {
  updateWhenIdle: true,
  updateWhenZooming: false,
  keepBuffer: 2,
};

const traceabilityExportUrl = (treeId: number, format: 'csv' | 'json' | 'pdf') =>
  getApiUrl(`/api/traceability/trees/${treeId}/export.${format}`);

interface TreeRecord {
  id: number;
  tree_code: string;
  crop_type: string;
  variety?: string;
  farmer_id?: number;
  farmer_name?: string;
  cohort_id?: number;
  cohort_name?: string;
  assigned_agronomist_id?: number;
  assigned_agronomist_name?: string;
  assigned_field_facilitator_id?: number;
  assigned_field_facilitator_name?: string;
  plot_code?: string;
  plot_name?: string;
  latitude?: number | string;
  longitude?: number | string;
  health_status: string;
  tree_status: string;
  production_status?: string;
  last_inspection_at?: string;
  open_issue_count?: number;
  latest_issue_photo?: string;
  latest_tree_photo?: string;
  observation_count?: number;
  latest_observation_at?: string;
  care_event_count?: number;
  latest_care_event_at?: string;
}

interface Summary {
  total_trees: number;
  active_trees: number;
  mapped_trees: number;
  missing_gps: number;
  needs_inspection: number;
  farmers: number;
  cohorts: number;
  harvest_lots: number;
  traceable_kg: number;
  public_certificates: number;
  open_risks: number;
}

interface FarmerOption {
  id: number;
  full_name?: string;
  name?: string;
  cohort_id?: number;
  household_type?: string;
  cohort_name?: string;
  plot_boundary?: any;
  plot_boundary_coordinates?: any;
  plot_area_hectares?: number | string;
  plot_size_hectares?: number | string;
}

interface StaffOption {
  id: number;
  full_name?: string;
  name?: string;
  email?: string;
  role?: string;
}

interface HarvestLot {
  id: number;
  lot_code: string;
  crop_type: string;
  harvest_date: string;
  total_quantity_kg: number | string;
  quality_grade?: string;
  public_trace_code?: string;
  traceability_status: string;
  tree_count: number;
}

interface FieldPlot {
  id: number;
  plot_code: string;
  name?: string;
  crop_type: string;
  farmer_id?: number;
  farmer_name?: string;
  primary_farmer_name?: string;
  farmers?: Array<{ id: number; name?: string; role_on_field?: string; is_primary?: boolean }>;
  farmer_count?: number;
  cohort_name?: string;
  assigned_agronomist_id?: number;
  assigned_agronomist_name?: string;
  assigned_field_facilitator_id?: number;
  assigned_field_facilitator_name?: string;
  expected_tree_count?: number;
  registered_tree_count?: number;
  mapped_tree_count?: number;
  grid_rows?: number;
  grid_columns?: number;
  center_lat?: number | string;
  center_lng?: number | string;
  area_hectares?: number | string;
  boundary_coordinates?: any;
}

const statusColor = (status: string) => {
  const value = String(status || '').toLowerCase();
  if (['critical', 'at_risk'].includes(value)) return 'bg-red-50 text-red-700 border-red-100';
  if (['watch', 'needs_inspection'].includes(value)) return 'bg-amber-50 text-amber-700 border-amber-100';
  if (['excellent', 'good', 'active', 'verified'].includes(value)) return 'bg-emerald-50 text-emerald-700 border-emerald-100';
  return 'bg-slate-50 text-slate-600 border-slate-100';
};

const fmt = (value: number | string | undefined) => new Intl.NumberFormat('en-US').format(Number(value || 0));
const cropPresets = ['avocado', 'macadamia', 'mango', 'coffee', 'citrus'] as const;
const healthOptions = ['excellent', 'good', 'watch', 'at_risk', 'critical'];
const careEventOptions = ['inspection', 'pruning', 'compost_application', 'irrigation', 'pest_control', 'training_visit'];
const labelize = (value: string) => value.replace(/_/g, ' ');
// Auto-size: shoelace area of the boundary polygon in hectares (local lat/lng meter projection).
const computeBoundaryAreaHectares = (boundary: Array<{ lat: number; lng: number }>) => {
  if (!Array.isArray(boundary) || boundary.length < 3) return null;
  const centerLat = boundary.reduce((sum, p) => sum + p.lat, 0) / boundary.length;
  const metersPerDegLat = 110540;
  const metersPerDegLng = 111320 * Math.cos((centerLat * Math.PI) / 180);
  let twiceArea = 0;
  for (let i = 0; i < boundary.length; i += 1) {
    const a = boundary[i];
    const b = boundary[(i + 1) % boundary.length];
    twiceArea += (a.lng * metersPerDegLng) * (b.lat * metersPerDegLat)
      - (b.lng * metersPerDegLng) * (a.lat * metersPerDegLat);
  }
  const hectares = Math.abs(twiceArea / 2) / 10000;
  return Number.isFinite(hectares) && hectares > 0 ? Number(hectares.toFixed(2)) : null;
};
const personName = (person: FarmerOption | StaffOption) => person.full_name || person.name || ('email' in person ? person.email : '') || `User ${person.id}`;
const parseMapBoundary = (value: any) => {
  if (!value) return [];
  let raw = value;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(raw)) return [];
  const points = raw
    .map((point: any) => Array.isArray(point)
      ? { lat: Number(point[0]), lng: Number(point[1]) }
      : { lat: Number(point.lat ?? point.latitude), lng: Number(point.lng ?? point.longitude) })
    .filter((point: any) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
  if (points.length > 3) {
    const first = points[0];
    const last = points[points.length - 1];
    if (Math.abs(first.lat - last.lat) < 0.0000005 && Math.abs(first.lng - last.lng) < 0.0000005) points.pop();
  }
  return points;
};

const pointInsideBoundary = (point: { lat: number; lng: number }, boundary: Array<{ lat: number; lng: number }>) => {
  if (boundary.length < 3) return true;
  let inside = false;
  for (let i = 0, j = boundary.length - 1; i < boundary.length; j = i++) {
    const xi = boundary[i].lng;
    const yi = boundary[i].lat;
    const xj = boundary[j].lng;
    const yj = boundary[j].lat;
    const intersects = ((yi > point.lat) !== (yj > point.lat))
      && (point.lng < ((xj - xi) * (point.lat - yi)) / ((yj - yi) || Number.EPSILON) + xi);
    if (intersects) inside = !inside;
  }
  return inside;
};

const TraceabilityPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('fields');
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [trees, setTrees] = useState<TreeRecord[]>([]);
  const [mapTrees, setMapTrees] = useState<TreeRecord[]>([]);
  const [lots, setLots] = useState<HarvestLot[]>([]);
  const [fields, setFields] = useState<FieldPlot[]>([]);
  const [farmers, setFarmers] = useState<FarmerOption[]>([]);
  const [mapFarmers, setMapFarmers] = useState<FarmerOption[]>([]);
  const [agronomists, setAgronomists] = useState<StaffOption[]>([]);
  const [query, setQuery] = useState('');
  const [selectedCrop, setSelectedCrop] = useState('');
  const [selectedTree, setSelectedTree] = useState<TreeRecord | null>(null);
  const [treeDetails, setTreeDetails] = useState<any>(null);
  const [showTreeForm, setShowTreeForm] = useState(false);
  const [showFieldForm, setShowFieldForm] = useState(false);
  const [editingField, setEditingField] = useState<FieldPlot | null>(null);
  const [showObservationForm, setShowObservationForm] = useState(false);
  const [showCareForm, setShowCareForm] = useState(false);
  const [showLotForm, setShowLotForm] = useState(false);
  const [baseLayer, setBaseLayer] = useState<'standard' | 'satellite' | 'terrain'>('satellite');
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [gridBuilderField, setGridBuilderField] = useState<FieldPlot | null>(null);
  const [gridStatuses, setGridStatuses] = useState<Record<number, any>>({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const results = await Promise.allSettled([
        apiGet<any>('/api/traceability/summary'),
        apiGet<any>('/api/traceability/plots'),
        apiGet<any>('/api/traceability/trees?limit=1000'),
        apiGet<any>('/api/traceability/harvest-lots'),
        apiGet<any>('/api/farmers'),
        apiGet<any>('/api/maps/farmers').catch(() => []),
        apiGet<any>('/api/users?role=agronomist'),
      ]);

      const valueAt = (index: number, fallback: any) =>
        results[index].status === 'fulfilled' ? results[index].value : fallback;
      const summaryData = valueAt(0, { summary: null });
      const fieldsData = valueAt(1, { plots: [] });
      const treesData = valueAt(2, { trees: [] });
      const lotsData = valueAt(3, { harvestLots: [] });
      const farmersData = valueAt(4, []);
      const mapFarmersData = valueAt(5, []);
      const agronomistsData = valueAt(6, []);

      setSummary(summaryData.summary);
      setFields(fieldsData.plots || []);
      setTrees(treesData.trees || []);
      setMapTrees(treesData.trees || []);
      setLots(lotsData.harvestLots || []);
      setFarmers(farmersData.farmers || farmersData || []);
      setMapFarmers(mapFarmersData || []);
      setAgronomists(agronomistsData.users || agronomistsData || []);

      const failedCount = results.filter(result => result.status === 'rejected').length;
      if (failedCount) {
        setError(`${failedCount} traceability data source${failedCount === 1 ? '' : 's'} could not be loaded. Available data is shown; retry to restore the rest.`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load traceability data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch authoritative grid status for each field
  useEffect(() => {
    if (!fields.length) return;
    const fetchStatuses = async () => {
      const statuses: Record<number, any> = {};
      const concurrency = 5;
      for (let index = 0; index < fields.length; index += concurrency) {
        const batch = fields.slice(index, index + concurrency);
        await Promise.all(
          batch.map(async (field) => {
          try {
            const data = await apiGet<any>(`/api/traceability/plots/${field.id}/grid-status`);
            statuses[field.id] = data;
          } catch {
            // Ignore errors — fall back to local counts
          }
          })
        );
      }
      setGridStatuses(statuses);
    };
    fetchStatuses();
  }, [fields]);

  const crops = useMemo(() => Array.from(new Set(trees.map(tree => tree.crop_type).filter(Boolean))), [trees]);
  const cropOptions = useMemo(() => Array.from(new Set([...cropPresets, ...crops])).filter(Boolean), [crops]);

  const filteredTrees = trees.filter(tree => {
    const search = query.toLowerCase();
    if (selectedCrop && tree.crop_type !== selectedCrop) return false;
    if (!search) return true;
    return [
      tree.tree_code,
      tree.crop_type,
      tree.variety,
      tree.farmer_name,
      tree.assigned_agronomist_name,
      tree.cohort_name,
      tree.plot_code,
      tree.health_status,
    ].some(value => String(value || '').toLowerCase().includes(search));
  });

  const filteredMapTrees = mapTrees.filter(tree => {
    const search = query.toLowerCase();
    if (selectedCrop && tree.crop_type !== selectedCrop) return false;
    if (!search) return true;
    return [
      tree.tree_code,
      tree.crop_type,
      tree.variety,
      tree.farmer_name,
      tree.assigned_agronomist_name,
      tree.cohort_name,
      tree.plot_code,
      tree.health_status,
    ].some(value => String(value || '').toLowerCase().includes(search));
  });

  const mappedTrees = useMemo(() => filteredMapTrees
    .map(tree => ({
      ...tree,
      latitude: Number(tree.latitude),
      longitude: Number(tree.longitude),
      health_status: tree.health_status || 'good',
      tree_status: tree.tree_status || 'active',
      open_issue_count: Number(tree.open_issue_count || 0),
      latest_issue_photo: tree.latest_issue_photo,
      latest_tree_photo: tree.latest_tree_photo,
    }))
    .filter(tree => Number.isFinite(tree.latitude) && Number.isFinite(tree.longitude)), [filteredMapTrees]);

  const mapLayerFarmers = useMemo(() => mapFarmers
    .map(farmer => ({
      id: Number(farmer.id),
      name: farmer.name || farmer.full_name || `Farmer ${farmer.id}`,
      household_type: farmer.household_type || 'farmer',
      plot_boundary_coordinates: parseMapBoundary(farmer.plot_boundary_coordinates || farmer.plot_boundary),
      cohort_name: '',
      cohort_id: 0,
      plot_area_hectares: Number(farmer.plot_area_hectares || farmer.plot_size_hectares || 0),
    }))
    .filter(farmer => farmer.plot_boundary_coordinates.length >= 3), [mapFarmers]);

  const visibleFarmers = mapLayerFarmers;
  const visibleCohorts: any[] = [];
  const visiblePlotCodes = useMemo(() => new Set(mappedTrees.map(tree => String(tree.plot_code || '')).filter(Boolean)), [mappedTrees]);
  const visibleFieldBoundaries = useMemo(() => fields
    .map(field => ({
      id: -Number(field.id),
      name: field.name || field.plot_code,
      plot_code: field.plot_code,
      household_type: 'field_plot',
      plot_boundary_coordinates: parseMapBoundary(field.boundary_coordinates),
      cohort_name: '',
      cohort_id: 0,
      plot_area_hectares: Number(field.area_hectares || 0),
    }))
    .filter(field => field.plot_boundary_coordinates.length >= 3)
    .filter(field => visiblePlotCodes.size === 0 || visiblePlotCodes.has(field.plot_code)), [fields, visiblePlotCodes]);

  const mapCenter = useMemo(() => {
    if (mappedTrees.length > 0) {
      return calculateCenter(mappedTrees.map(tree => ({ lat: tree.latitude, lng: tree.longitude })));
    }
    const fieldCenters = fields
      .map(field => ({ lat: Number(field.center_lat), lng: Number(field.center_lng) }))
      .filter(point => Number.isFinite(point.lat) && Number.isFinite(point.lng));
    return fieldCenters.length > 0 ? calculateCenter(fieldCenters) : RWANDA_DEFAULTS.center;
  }, [fields, mappedTrees]);

  const mapZoom = mappedTrees.length > 0 ? 16 : 8;

  const loadTreeDetails = async (tree: TreeRecord) => {
    setSelectedTree(tree);
    try {
      const details = await apiGet<any>(`/api/traceability/trees/${tree.id}`);
      setTreeDetails(details);
    } catch {
      setTreeDetails(null);
    }
  };

  const submitTree = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    const form = new FormData(event.currentTarget);
    try {
      await apiPost('/api/traceability/trees', Object.fromEntries(form.entries()));
      setShowTreeForm(false);
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to create tree');
    } finally {
      setSubmitting(false);
    }
  };

  const generateGridWithRecovery = async (
    plotId: number,
    payload: Record<string, any>,
    requestedCount: number,
  ) => {
    try {
      return await apiPost<any>(`/api/traceability/plots/${plotId}/generate-trees`, payload);
    } catch (generationError) {
      try {
        const status = await apiGet<any>(`/api/traceability/plots/${plotId}/grid-status`);
        if (
          Number(status.created || 0) >= requestedCount
          && Number(status.mapped || 0) >= requestedCount
          && Number(status.invalid || 0) === 0
        ) {
          return {
            success: true,
            recovered: true,
            plot_code: status.field_code,
            created_count: status.created,
            mapped_count: status.mapped,
            total_after: status.total_after,
            trees: [],
          };
        }
      } catch {
        // The original generation failure is more useful than recovery noise.
      }
      throw generationError;
    }
  };

  const submitField = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    const form = new FormData(event.currentTarget);
    const payload = Object.fromEntries(form.entries()) as Record<string, any>;
    const farmerIds = form.getAll('farmer_ids').map((value) => Number(value)).filter(Boolean);
    const shouldGenerate = Number(payload.expected_tree_count) > 0;
    let focusPlotCode = '';
    let generatedTrees: TreeRecord[] = [];

    try {
      const targetPlotId = Number(payload.target_plot_id) || 0;
      if (targetPlotId) {
        const focusPlot = fields.find(field => Number(field.id) === targetPlotId);
        focusPlotCode = focusPlot?.plot_code || '';
        // Insert/align trees into an EXISTING mapped field — no new field is created.
        if (!(Number(payload.expected_tree_count) > 0)) throw new Error('Set Expected trees first.');
        const existingBoundary = parseMapBoundary(payload.boundary_coordinates);
        if (existingBoundary.length < 3) {
          throw new Error('This mapped field has no saved boundary. Open Farm Maps, edit/draw the field boundary, then return to Traceability to align trees.');
        }
        const generated = await generateGridWithRecovery(targetPlotId, {
          tree_count: payload.expected_tree_count,
          crop_type: payload.crop_type,
          variety: payload.variety,
          grid_rows: payload.grid_rows,
          grid_columns: payload.grid_columns,
          row_spacing_m: payload.row_spacing_m,
          tree_spacing_m: payload.tree_spacing_m,
          boundary_coordinates: payload.boundary_coordinates,
          center_lat: payload.center_lat,
          center_lng: payload.center_lng,
          assigned_agronomist_id: payload.assigned_agronomist_id,
        }, Number(payload.expected_tree_count));
        focusPlotCode = generated?.plot_code || focusPlotCode;
        generatedTrees = Array.isArray(generated?.trees) ? generated.trees : [];
      } else {
        const targetFarmerIds = farmerIds.length ? farmerIds : (payload.farmer_id ? [Number(payload.farmer_id)] : []);
        if (targetFarmerIds.length === 0) throw new Error('Select at least one farmer.');
        // Field-first model: ONE mapped field, MANY assigned farmers. Never duplicate the boundary per farmer.
        const data = await apiPost<any>('/api/traceability/plots', {
          ...payload,
          farmer_ids: targetFarmerIds,
          primary_farmer_id: Number(payload.primary_farmer_id) || targetFarmerIds[0],
        });
        focusPlotCode = data.plot?.plot_code || '';
        if (shouldGenerate && data.plot?.id) {
          const generated = await generateGridWithRecovery(data.plot.id, {
            tree_count: payload.expected_tree_count,
            crop_type: payload.crop_type,
            variety: payload.variety,
            grid_rows: payload.grid_rows,
            grid_columns: payload.grid_columns,
            row_spacing_m: payload.row_spacing_m,
            tree_spacing_m: payload.tree_spacing_m,
            boundary_coordinates: payload.boundary_coordinates,
            center_lat: payload.center_lat,
            center_lng: payload.center_lng,
            assigned_agronomist_id: payload.assigned_agronomist_id,
          }, Number(payload.expected_tree_count));
          focusPlotCode = generated?.plot_code || focusPlotCode;
          generatedTrees = Array.isArray(generated?.trees) ? generated.trees : [];
        }
      }
      setShowFieldForm(false);
      if (generatedTrees.length > 0) {
        setMapTrees(prev => {
          const existingIds = new Set(prev.map(tree => tree.id));
          return [...generatedTrees.filter(tree => !existingIds.has(tree.id)), ...prev];
        });
        setTrees(prev => {
          const existingIds = new Set(prev.map(tree => tree.id));
          return [...generatedTrees.filter(tree => !existingIds.has(tree.id)), ...prev];
        });
      }
      await fetchData();
      if (focusPlotCode) {
        setQuery(focusPlotCode);
        setActiveTab('map');
      } else if (generatedTrees.length > 0) {
        setActiveTab('map');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create field');
    } finally {
      setSubmitting(false);
    }
  };

  const submitTreeAssignment = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedTree) return;
    setSubmitting(true);
    setError('');
    const form = new FormData(event.currentTarget);
    const payload = Object.fromEntries(form.entries()) as Record<string, any>;
    try {
      await apiPatch('/api/traceability/trees/assign', {
        tree_ids: [selectedTree.id],
        farmer_id: payload.farmer_id || null,
        assigned_agronomist_id: payload.assigned_agronomist_id || null,
        crop_type: payload.crop_type || selectedTree.crop_type,
      });
      await fetchData();
      await loadTreeDetails(selectedTree);
    } catch (err: any) {
      setError(err.message || 'Failed to assign tree');
    } finally {
      setSubmitting(false);
    }
  };

  const submitFieldEdit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingField) return;
    setSubmitting(true);
    setError('');
    const form = new FormData(event.currentTarget);
    const payload: Record<string, any> = Object.fromEntries(form.entries());
    // Field-first: replace the whole farmer team in one call (backend swaps memberships atomically).
    const farmerIds = form.getAll('farmer_ids').map((value) => Number(value)).filter(Boolean);
    if (farmerIds.length) {
      payload.farmer_ids = farmerIds;
      payload.primary_farmer_id = Number(payload.primary_farmer_id) || farmerIds[0];
    } else {
      delete payload.primary_farmer_id;
    }
    try {
      await apiPatch(`/api/traceability/plots/${editingField.id}`, payload);
      setEditingField(null);
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to update field batch');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteField = async (field: FieldPlot) => {
    const confirmed = window.confirm(`Delete batch ${field.plot_code}? All trees generated under this batch and linked batch records will be removed from KPIs and maps.`);
    if (!confirmed) return;
    setSubmitting(true);
    try {
      await apiDelete(`/api/traceability/plots/${field.id}`);
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete field batch');
    } finally {
      setSubmitting(false);
    }
  };

  const mapFieldTrees = (field: FieldPlot) => {
    setGridBuilderField(field);
  };

  const submitObservation = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedTree) return;
    setSubmitting(true);
    const form = new FormData(event.currentTarget);
    try {
      await apiPost(`/api/traceability/trees/${selectedTree.id}/observations`, Object.fromEntries(form.entries()));
      setShowObservationForm(false);
      await fetchData();
      await loadTreeDetails(selectedTree);
    } catch (err: any) {
      setError(err.message || 'Failed to add observation');
    } finally {
      setSubmitting(false);
    }
  };

  const submitCare = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedTree) return;
    setSubmitting(true);
    const form = new FormData(event.currentTarget);
    try {
      await apiPost(`/api/traceability/trees/${selectedTree.id}/care-events`, Object.fromEntries(form.entries()));
      setShowCareForm(false);
      await loadTreeDetails(selectedTree);
    } catch (err: any) {
      setError(err.message || 'Failed to add care event');
    } finally {
      setSubmitting(false);
    }
  };

  const verifyRecord = async (type: 'observation' | 'care-event', recordId: number, status: string, reason?: string) => {
    if (!selectedTree) return;
    setVerifying(true);
    try {
      const path = type === 'observation'
        ? `/api/traceability/trees/${selectedTree.id}/observations/${recordId}/verify`
        : `/api/traceability/trees/${selectedTree.id}/care-events/${recordId}/verify`;
      await apiPatch(path, { status, rejection_reason: reason || null });
      await loadTreeDetails(selectedTree);
    } catch (err: any) {
      setError(err.message || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const submitLot = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    const form = new FormData(event.currentTarget);
    const selectedTreeIds = trees
      .filter(tree => form.get(`tree_${tree.id}`) === 'on')
      .map(tree => tree.id);

    try {
      await apiPost('/api/traceability/harvest-lots', {
        ...Object.fromEntries(form.entries()),
        tree_ids: selectedTreeIds,
      });
      setShowLotForm(false);
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to create harvest lot');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="text-center">
          <ArrowPathIcon className="w-12 h-12 text-emerald-600 animate-spin mx-auto" />
          <p className="mt-3 text-sm font-medium text-slate-500">Loading traceability...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/20 to-slate-100 p-4 lg:p-6">
      <div className="max-w-7xl mx-auto space-y-5">
        {/* ═══════ HERO HEADER ═══════ */}
        <div className="relative overflow-hidden bg-white border border-slate-100 rounded-2xl p-5 lg:p-6 shadow-sm">
          <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-emerald-100/50 to-cyan-100/50 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center shadow-lg shadow-emerald-600/20">
                <QrCodeIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-slate-900">Traceability</h1>
                <p className="text-sm text-slate-500">Farm maps, tree records, harvest history, and mobile field updates</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowFieldForm(true)}
                className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50 hover:border-emerald-200 flex items-center gap-2 transition-all"
              >
                <MapPinIcon className="w-4 h-4" />
                Map field
              </button>
              <button
                onClick={() => setShowLotForm(true)}
                className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50 hover:border-emerald-200 flex items-center gap-2 transition-all"
              >
                <ClipboardDocumentCheckIcon className="w-4 h-4" />
                Harvest lot
              </button>
              <button
                onClick={() => setShowTreeForm(true)}
                className="px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 flex items-center gap-2 shadow-lg shadow-emerald-200 transition-all"
              >
                <PlusIcon className="w-4 h-4" />
                Add tree
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-xl text-sm">
            {error}
          </div>
        )}

        {/* ═══════ KPI CARDS ═══════ */}
        <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-6 gap-2 lg:gap-3">
          {[
            { label: 'Trees', value: summary?.total_trees, icon: Squares2X2Icon, color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Mapped', value: summary?.mapped_trees, icon: MapPinIcon, color: 'text-blue-600 bg-blue-50' },
            { label: 'Farmers', value: summary?.farmers, icon: CheckCircleIcon, color: 'text-purple-600 bg-purple-50' },
            { label: 'Risks', value: summary?.open_risks, icon: BeakerIcon, color: 'text-red-600 bg-red-50' },
            { label: 'Lots', value: summary?.harvest_lots, icon: ClipboardDocumentCheckIcon, color: 'text-amber-600 bg-amber-50' },
            { label: 'Kg', value: summary?.traceable_kg, icon: QrCodeIcon, color: 'text-indigo-600 bg-indigo-50' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="bg-white border border-slate-100 rounded-xl p-3 lg:p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
              </div>
              <p className="text-xl lg:text-2xl font-bold text-slate-900">{fmt(value)}</p>
            </div>
          ))}
        </div>

        {/* ═══════ MAIN CONTENT CARD ═══════ */}
        <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
          <div className="border-b border-slate-100 px-4 py-3 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="flex gap-1 overflow-x-auto scrollbar-none">
              {([
                ['fields', 'Farm Fields', MapPinIcon],
                ['trees', 'Tree Registry', Squares2X2Icon],
                ['map', 'Map View', MapPinIcon],
                ['lots', 'Harvest Lots', ClipboardDocumentCheckIcon],
              ] as const).map(([key, label, Icon]) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`flex items-center gap-1.5 px-3 lg:px-4 py-2 rounded-xl text-xs lg:text-sm font-semibold whitespace-nowrap transition-all ${
                    activeTab === key 
                      ? 'bg-emerald-600 text-white shadow-md shadow-emerald-200' 
                      : 'bg-slate-50 text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1 min-w-[200px]">
                <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search trees, fields, farmers..."
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 transition-all"
                />
              </div>
              <select
                value={selectedCrop}
                onChange={(event) => setSelectedCrop(event.target.value)}
                className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 transition-all"
              >
                <option value="">All crops</option>
                {crops.map(crop => <option key={crop} value={crop}>{crop}</option>)}
              </select>
            </div>
          </div>

          {activeTab === 'fields' && (
            <div className="grid lg:grid-cols-3 gap-4 p-4">
              {fields.length === 0 ? (
                <EmptyState
                  icon={MapPinIcon}
                  title="No farm fields mapped"
                  text="Map a farm field first, then generate crops or trees inside that boundary."
                  action="Map farm field"
                  onAction={() => setShowFieldForm(true)}
                />
              ) : fields.map(field => {
                const gs = gridStatuses[field.id];
                const expected = gs?.expected ?? Number(field.expected_tree_count || 0);
                const registered = gs?.created ?? Number(field.registered_tree_count || 0);
                const mapped = gs?.mapped ?? Number(field.mapped_tree_count || 0);
                const insideBoundary = gs?.inside_boundary ?? mapped;
                const invalid = gs?.invalid ?? 0;
                const gridStatus = gs?.status || (expected > 0 && registered === expected && mapped >= expected && invalid === 0 ? 'aligned' : 'pending');
                const boundary = parseMapBoundary(field.boundary_coordinates);
                const hasFieldLocation = boundary.length >= 3
                  || (Number.isFinite(Number(field.center_lat)) && Number.isFinite(Number(field.center_lng)));
                const isComplete = gridStatus === 'aligned';
                const needsTreeTopUp = expected > registered;
                const remainingTrees = Math.max(expected - registered, 0);
                const canAlign = !isComplete && (registered > 0 || expected > 0) && hasFieldLocation;
                const openFarmMaps = () => {
                  window.location.href = `/dashboard/maps?field=${encodeURIComponent(String(field.id))}`;
                };
                const progress = expected > 0 ? Math.min((registered / expected) * 100, 100) : 0;
                return (
                  <article
                    key={field.id}
                    onClick={() => {
                      setActiveTab('trees');
                      setQuery(field.plot_code);
                    }}
                    className="cursor-pointer text-left border border-slate-100 rounded-xl p-4 bg-white hover:border-emerald-200 hover:bg-emerald-50/30 transition"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <MapPinIcon className="w-5 h-5 text-emerald-600" />
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {!hasFieldLocation && (
                          <span className="px-2 py-1 rounded-full text-[11px] border font-black uppercase tracking-wide bg-amber-50 text-amber-700 border-amber-100">
                            Needs boundary
                          </span>
                        )}
                        <span className="px-2 py-1 rounded-full text-[11px] border font-semibold capitalize bg-emerald-50 text-emerald-700 border-emerald-100">
                          {field.crop_type}
                        </span>
                      </div>
                    </div>
                    <p className="mt-3 font-bold text-slate-900">{field.name || field.plot_code}</p>
                    <p className="text-xs font-mono text-slate-500">{field.plot_code}</p>
                    <div className="mt-4 space-y-3">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Trees</span>
                        <span className="font-bold text-slate-700">{registered}/{expected || registered}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Inside boundary</span>
                        <span className={`font-bold ${insideBoundary < registered ? 'text-amber-600' : 'text-emerald-700'}`}>{insideBoundary}/{registered}</span>
                      </div>
                      {invalid > 0 && (
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-500">Invalid</span>
                          <span className="font-bold text-red-600">{invalid}</span>
                        </div>
                      )}
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full bg-emerald-600 rounded-full" style={{ width: `${expected ? progress : 100}%` }} />
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <span className="bg-slate-50 rounded-lg p-2">{field.grid_rows || '-'} rows</span>
                        <span className="bg-slate-50 rounded-lg p-2">{field.grid_columns || '-'} columns</span>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-3 text-xs leading-5">
                        <p className="font-semibold text-slate-700">
                          {field.farmer_name || 'No farmer assigned'}
                          {Number(field.farmer_count || (Array.isArray(field.farmers) ? field.farmers.length : 0)) > 1 && (
                            <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-emerald-700">
                              +{Number(field.farmer_count || (Array.isArray(field.farmers) ? field.farmers.length : 0)) - 1} more
                            </span>
                          )}
                        </p>
                        <p className={hasFieldLocation ? 'text-slate-500' : 'font-semibold text-amber-700'}>
                          {hasFieldLocation
                            ? `${field.area_hectares ? `${field.area_hectares} ha` : 'Mapped field'} · Agronomist: ${field.assigned_agronomist_name || 'Not assigned'}`
                            : 'No saved boundary or center point · open Farm Maps to draw it'}
                        </p>
                      </div>
                      <div className="flex gap-2 pt-2" onClick={(event) => event.stopPropagation()}>
                        <button
                          type="button"
                          disabled={(hasFieldLocation && !canAlign) || submitting}
                          title={isComplete
                            ? `${mapped} trees already aligned`
                            : !hasFieldLocation
                              ? 'Open Farm Maps and draw this field boundary before generating trees'
                              : needsTreeTopUp
                                ? `Generate ${remainingTrees} remaining trees inside this field boundary`
                              : registered > 0
                                ? 'Place existing tree records inside this field boundary'
                                : expected > 0
                                  ? 'Generate and place the expected trees inside this field boundary'
                                  : 'Set Expected trees first, then align this field'}
                          onClick={() => {
                            if (!hasFieldLocation) {
                              openFarmMaps();
                              return;
                            }
                            mapFieldTrees(field);
                          }}
                          className="flex-1 rounded-lg border border-emerald-100 px-3 py-2 text-xs font-bold text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
                        >
                          {!hasFieldLocation ? (
                            <>
                              <MapPinIcon className="mr-1 inline h-4 w-4" />
                              Add boundary
                            </>
                          ) : isComplete ? (
                            <>
                              <CheckCircleIcon className="mr-1 inline h-4 w-4 text-emerald-600" />
                              {mapped}/{expected} mapped
                            </>
                          ) : needsTreeTopUp ? (
                            <>
                              <PlusIcon className="mr-1 inline h-4 w-4" />
                              Add {remainingTrees} trees
                            </>
                          ) : registered <= 0 && expected > 0 ? (
                            <>
                              <PlusIcon className="mr-1 inline h-4 w-4" />
                              Generate {expected} trees
                            </>
                          ) : (
                            <>
                              <ArrowPathIcon className="mr-1 inline h-4 w-4" />
                              Open grid builder
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingField(field)}
                          className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700 hover:border-emerald-300 hover:text-emerald-700"
                        >
                          <PencilSquareIcon className="mr-1 inline h-4 w-4" />
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteField(field)}
                          className="flex-1 rounded-lg border border-red-100 px-3 py-2 text-xs font-bold text-red-600 hover:bg-red-50"
                        >
                          <TrashIcon className="mr-1 inline h-4 w-4" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {activeTab === 'trees' && (
            <div className="overflow-auto max-h-[600px]">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Tree</th>
                    <th className="text-left px-4 py-3 font-semibold">Farmer</th>
                    <th className="text-left px-4 py-3 font-semibold">Crop</th>
                    <th className="text-left px-4 py-3 font-semibold">Health</th>
                    <th className="text-left px-4 py-3 font-semibold">Mobile</th>
                    <th className="text-left px-4 py-3 font-semibold">GPS</th>
                    <th className="text-right px-4 py-3 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredTrees.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <EmptyState
                          icon={Squares2X2Icon}
                          title="No trees found"
                          text="Generate a field grid or adjust your filters."
                          action="Field grid"
                          onAction={() => setShowFieldForm(true)}
                        />
                      </td>
                    </tr>
                  ) : filteredTrees.map(tree => (
                    <tr key={tree.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <p className="font-bold text-slate-900">{tree.tree_code}</p>
                        <p className="text-xs text-slate-500">{tree.plot_code || tree.cohort_name || 'No field'}</p>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        <p className="font-medium">{tree.farmer_name || 'Not linked'}</p>
                        <p className="text-xs text-slate-400">Agronomist: {tree.assigned_agronomist_name || 'Not assigned'}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="capitalize font-semibold text-slate-800">{tree.crop_type}</p>
                        <p className="text-xs text-slate-500">{tree.variety || 'No variety'}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs border font-semibold capitalize ${statusColor(tree.health_status)}`}>
                          {tree.health_status?.replace('_', ' ') || 'good'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {((tree.observation_count || 0) > 0 || (tree.care_event_count || 0) > 0) ? (
                          <div className="flex flex-col gap-0.5">
                            {(tree.observation_count || 0) > 0 && (
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                {tree.observation_count || 0} obs
                              </span>
                            )}
                            {(tree.care_event_count || 0) > 0 && (
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700">
                                <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                                {tree.care_event_count || 0} care
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {tree.latitude && tree.longitude ? 'Mapped' : 'Missing'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => loadTreeDetails(tree)}
                          className="p-2 rounded-lg hover:bg-emerald-50 text-emerald-700"
                          title="View"
                        >
                          <EyeIcon className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'map' && (
            <div className="p-4">
              <div className="grid gap-4 xl:grid-cols-[1fr_340px]">
                <div className="overflow-hidden rounded-2xl border border-slate-100 bg-slate-900 shadow-sm">
                  <div className="flex flex-col gap-3 border-b border-white/10 bg-slate-950 px-4 py-3 text-white md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-300">Live tree map</p>
                      <h3 className="mt-1 text-lg font-black">Click any tree to inspect it</h3>
                    </div>
                    <div className="flex gap-2">
                      {(['satellite', 'standard', 'terrain'] as const).map(layer => (
                        <button
                          key={layer}
                          onClick={() => setBaseLayer(layer)}
                          className={`rounded-lg px-3 py-2 text-xs font-bold capitalize transition ${baseLayer === layer ? 'bg-emerald-500 text-white' : 'bg-white/10 text-white/70 hover:bg-white/15'}`}
                        >
                          {layer}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="h-[560px] bg-slate-100">
                    {mappedTrees.length === 0 && visibleFieldBoundaries.length === 0 ? (
                      <div className="flex h-full flex-col items-center justify-center px-6 text-center">
                        <MapPinIcon className="h-10 w-10 text-slate-300" />
                        <h3 className="mt-4 text-base font-black text-slate-800">No mapped trees yet</h3>
                        <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">
                          Generate a mapped field grid, or add a single tree by choosing a mapped farm field and clicking its location on the map.
                        </p>
                        <button
                          onClick={() => setShowFieldForm(true)}
                          className="mt-5 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-700"
                        >
                          Create mapped field grid
                        </button>
                      </div>
                    ) : (
                      <MapBaseComponent
                        farmers={visibleFarmers}
                        fieldPlots={visibleFieldBoundaries}
                        cohorts={visibleCohorts}
                        warehouses={[]}
                        trees={mappedTrees}
                        center={mapCenter}
                        zoom={mapZoom}
                        baseLayer={baseLayer}
                        sizeToken={`${activeTab}-${mappedTrees.length}-${baseLayer}`}
                        focusedTreeId={selectedTree?.id || null}
                        onTreeClick={(tree) => {
                          const source = trees.find(item => item.id === tree.id);
                          loadTreeDetails(source || tree);
                        }}
                      />
                    )}
                  </div>
                </div>

                <aside className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-base font-black text-slate-900">Mapped trees</h3>
                      <p className="text-xs text-slate-500">{mappedTrees.length} trees and {visibleFieldBoundaries.length} fields visible</p>
                    </div>
                    <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">
                      {fmt(summary?.mapped_trees || mappedTrees.length)}
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {healthOptions.map(status => {
                      const count = mappedTrees.filter(tree => tree.health_status === status).length;
                      return (
                        <div key={status} className="rounded-xl bg-slate-50 p-3">
                          <p className="text-lg font-black text-slate-900">{count}</p>
                          <p className="text-[11px] font-bold uppercase text-slate-500">{labelize(status)}</p>
                        </div>
                      );
                    })}
                    <div className="rounded-xl bg-red-50 p-3">
                      <p className="text-lg font-black text-red-700">{mappedTrees.filter(tree => Number(tree.open_issue_count || 0) > 0).length}</p>
                      <p className="text-[11px] font-bold uppercase text-red-500">Reported</p>
                    </div>
                  </div>

                  <div className="mt-5 max-h-[520px] space-y-2 overflow-y-auto pr-1">
                    {mappedTrees.map(tree => (
                      <button
                        key={tree.id}
                        onClick={() => {
                          const source = trees.find(item => item.id === tree.id);
                          loadTreeDetails(source || tree);
                        }}
                        className="w-full rounded-xl border border-slate-100 p-3 text-left transition hover:border-emerald-200 hover:bg-emerald-50"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="font-bold text-slate-900">{tree.tree_code}</p>
                            <p className="text-xs text-slate-500 capitalize">{tree.crop_type}{tree.variety ? ` - ${tree.variety}` : ''}</p>
                          </div>
                          <span className={`shrink-0 rounded-full border px-2 py-1 text-[11px] font-bold capitalize ${statusColor(tree.health_status)}`}>
                            {Number(tree.open_issue_count || 0) > 0 ? 'Reported' : labelize(tree.health_status)}
                          </span>
                        </div>
                        <p className="mt-2 text-[11px] font-mono text-slate-400">
                          {tree.latitude.toFixed(6)}, {tree.longitude.toFixed(6)}
                        </p>
                      </button>
                    ))}
                  </div>
                </aside>
              </div>
            </div>
          )}

          {activeTab === 'lots' && (
            <div className="grid lg:grid-cols-3 gap-4 p-4">
              {lots.length === 0 ? (
                <EmptyState
                  icon={ClipboardDocumentCheckIcon}
                  title="No harvest lots yet"
                  text="Create lots from selected tree records when harvesting starts."
                  action="Create lot"
                  onAction={() => setShowLotForm(true)}
                />
              ) : lots.map(lot => (
                <div key={lot.id} className="border border-slate-100 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <ClipboardDocumentCheckIcon className="w-5 h-5 text-emerald-600" />
                    <span className={`px-2 py-1 rounded-full text-[11px] border font-semibold capitalize ${statusColor(lot.traceability_status)}`}>
                      {lot.traceability_status}
                    </span>
                  </div>
                  <p className="mt-3 font-bold text-slate-900">{lot.lot_code}</p>
                  <p className="text-sm text-slate-500 capitalize">{lot.crop_type} - {fmt(lot.total_quantity_kg)} kg</p>
                  <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                    <span className="bg-slate-50 rounded-lg p-2">{lot.tree_count || 0} trees</span>
                    <span className="bg-slate-50 rounded-lg p-2">{lot.public_trace_code ? 'QR ready' : 'No QR'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedTree && (
        <SidePanel
          tree={selectedTree}
          details={treeDetails}
          onClose={() => { setSelectedTree(null); setTreeDetails(null); }}
          onObserve={() => setShowObservationForm(true)}
          onCare={() => setShowCareForm(true)}
          farmers={farmers}
          agronomists={agronomists}
          cropOptions={cropOptions}
          loading={submitting}
          onAssign={submitTreeAssignment}
          onVerify={verifyRecord}
          verifying={verifying}
        />
      )}

      {showTreeForm && (
        <AddTreeModal
          farmers={farmers}
          mapFarmers={mapFarmers}
          fields={fields}
          agronomists={agronomists}
          cropOptions={cropOptions}
          loading={submitting}
          onClose={() => setShowTreeForm(false)}
          onSubmit={submitTree}
        />
      )}

      {showFieldForm && (
        <FieldGridModal
          farmers={farmers}
          mapFarmers={mapFarmers}
          plots={fields}
          agronomists={agronomists}
          cropOptions={cropOptions}
          loading={submitting}
          onClose={() => setShowFieldForm(false)}
          onSubmit={submitField}
        />
      )}

      {editingField && (
        <EditFieldModal
          field={editingField}
          farmers={farmers}
          agronomists={agronomists}
          loading={submitting}
          onClose={() => setEditingField(null)}
          onSubmit={submitFieldEdit}
        />
      )}

      {showObservationForm && selectedTree && (
        <Modal title="Record observation" onClose={() => setShowObservationForm(false)} wide>
          <form onSubmit={submitObservation} className="space-y-4">
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3">
              <p className="text-xs font-bold text-emerald-800">Observation for {selectedTree.tree_code}</p>
            </div>
            <p className="text-xs font-bold uppercase text-slate-400">Health &amp; Vigor</p>
            <FormGrid>
              <Field label="Date" name="observation_date" type="date" defaultValue={new Date().toISOString().slice(0, 10)} />
              <SelectField label="Health Status" name="health_status" options={healthOptions.map(v => ({ value: v, label: labelize(v) }))} />
              <Field label="Canopy Condition" name="canopy_condition" placeholder="full, sparse, yellowing..." />
              <SelectField label="Canopy Vigor" name="canopy_vigor" options={['excellent', 'good', 'moderate', 'poor', 'dead'].map(v => ({ value: v, label: labelize(v) }))} />
              <SelectField label="Leaf Color" name="leaf_color" options={['green', 'yellow', 'brown', 'dark_green', 'pale', 'spotted'].map(v => ({ value: v, label: labelize(v) }))} />
              <Field label="Fruit Size (mm)" name="fruit_size_diameter" type="number" step="0.1" />
            </FormGrid>
            <p className="text-xs font-bold uppercase text-slate-400">Pest &amp; Disease</p>
            <FormGrid>
              <Field label="Pest Signs" name="pest_signs" placeholder="aphids, borer, mites..." />
              <Field label="Pest Type" name="pest_type" placeholder="insect, rodent, nematode..." />
              <Field label="Disease Signs" name="disease_signs" placeholder="powdery mildew, canker..." />
              <Field label="Disease Type" name="disease_type" placeholder="fungal, bacterial, viral..." />
              <SelectField label="Severity" name="severity_score" options={['1', '2', '3', '4', '5'].map(v => ({ value: v, label: v === '1' ? '1 - Mild' : v === '2' ? '2 - Light' : v === '3' ? '3 - Moderate' : v === '4' ? '4 - Severe' : '5 - Critical' }))} />
            </FormGrid>
            <p className="text-xs font-bold uppercase text-slate-400">Soil &amp; Environment</p>
            <FormGrid>
              <Field label="Weed Pressure" name="weed_pressure" placeholder="none, low, moderate, high" />
              <SelectField label="Erosion Status" name="erosion_status" options={['none', 'minor', 'moderate', 'severe'].map(v => ({ value: v, label: labelize(v) }))} />
              <SelectField label="Drainage" name="drainage_status" options={['good', 'moderate', 'poor', 'waterlogged'].map(v => ({ value: v, label: labelize(v) }))} />
              <Field label="Soil Condition" name="soil_condition" placeholder="dry, moist, wet..." />
            </FormGrid>
            <p className="text-xs font-bold uppercase text-slate-400">Yield &amp; Follow-up</p>
            <FormGrid>
              <Field label="Estimated Yield (kg)" name="estimated_yield_kg" type="number" step="0.1" />
              <SelectField label="Urgency" name="urgency" options={['low', 'medium', 'high', 'critical'].map(v => ({ value: v, label: labelize(v) }))} />
              <label className="flex items-center gap-2 rounded-xl bg-slate-50 border border-slate-100 px-3 py-3 text-sm">
                <input type="checkbox" name="follow_up_required" className="rounded border-slate-300" />
                <span className="font-semibold text-slate-700">Follow-up required</span>
              </label>
              <Field label="Follow-up Date" name="follow_up_date" type="date" />
            </FormGrid>
            <p className="text-xs font-bold uppercase text-slate-400">Location &amp; Photo</p>
            <FormGrid>
              <Field label="Tree image URL" name="photo_url" placeholder="/uploads/trees/photo.jpg" />
              <Field label="GPS Accuracy (m)" name="gps_accuracy_m" type="number" step="0.1" />
            </FormGrid>
            <TextArea label="Recommendation" name="recommendation" />
            <ModalActions loading={submitting} submitLabel="Save observation" onCancel={() => setShowObservationForm(false)} />
          </form>
        </Modal>
      )}

      {showCareForm && selectedTree && (
        <Modal title="Record care event" onClose={() => setShowCareForm(false)} wide>
          <form onSubmit={submitCare} className="space-y-4">
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-3">
              <p className="text-xs font-bold text-blue-800">Care event for {selectedTree.tree_code}</p>
            </div>
            <p className="text-xs font-bold uppercase text-slate-400">Activity</p>
            <FormGrid>
              <SelectField label="Event Type" name="event_type" required options={careEventOptions.map(v => ({ value: v, label: labelize(v) }))} />
              <SelectField label="Category" name="activity_category" options={['canopy_management', 'soil_management', 'water_management', 'pest_management', 'harvest', 'post_harvest', 'training', 'other'].map(v => ({ value: v, label: labelize(v) }))} />
              <Field label="Date" name="event_date" type="date" defaultValue={new Date().toISOString().slice(0, 10)} />
            </FormGrid>
            <p className="text-xs font-bold uppercase text-slate-400">Input / Product</p>
            <FormGrid>
              <Field label="Input Name" name="input_name" placeholder="Compost, Neem oil..." />
              <Field label="Active Ingredient" name="active_ingredient" />
              <Field label="Supplier" name="supplier" />
              <Field label="Batch Number" name="input_batch_number" />
              <Field label="Quantity" name="input_quantity" type="number" step="0.1" />
              <SelectField label="Unit" name="input_unit" options={['kg', 'g', 'L', 'mL', 'pcs', 'bags', 'loads'].map(v => ({ value: v, label: v }))} />
              <Field label="Dilution Rate" name="dilution_rate" placeholder="1:100" />
              <Field label="Qty per Tree" name="quantity_per_tree" type="number" step="0.01" />
              <Field label="Qty per Plot" name="quantity_per_plot" type="number" step="0.01" />
            </FormGrid>
            <p className="text-xs font-bold uppercase text-slate-400">Labor &amp; Cost</p>
            <FormGrid>
              <Field label="Worker Name" name="worker_name" />
              <Field label="Labor Hours" name="labor_hours" type="number" step="0.5" />
              <Field label="Labor Cost (RWF)" name="labor_cost" type="number" />
              <Field label="Total Cost (RWF)" name="cost_rwf" type="number" />
            </FormGrid>
            <p className="text-xs font-bold uppercase text-slate-400">Safety &amp; Intervals</p>
            <FormGrid>
              <Field label="Pre-Harvest Interval (days)" name="pre_harvest_interval_days" type="number" />
              <Field label="Re-entry Interval (hours)" name="re_entry_interval_hours" type="number" />
              <Field label="Protective Equipment" name="protective_equipment" placeholder="gloves, mask, suit..." />
            </FormGrid>
            <p className="text-xs font-bold uppercase text-slate-400">Result &amp; Follow-up</p>
            <FormGrid>
              <Field label="Result After Care" name="result_after_care" placeholder="effective, partial, no change..." />
              <Field label="Next Action Date" name="next_action_date" type="date" />
              <Field label="Tree image URL" name="photo_url" placeholder="/uploads/care/photo.jpg" />
              <Field label="GPS Accuracy (m)" name="gps_accuracy_m" type="number" step="0.1" />
            </FormGrid>
            <TextArea label="Notes" name="notes" />
            <ModalActions loading={submitting} submitLabel="Save care event" onCancel={() => setShowCareForm(false)} />
          </form>
        </Modal>
      )}

      {showLotForm && (
        <Modal title="Create harvest lot" onClose={() => setShowLotForm(false)} wide>
          <form onSubmit={submitLot} className="space-y-4">
            <FormGrid>
              <Field label="Crop type" name="crop_type" defaultValue={selectedCrop || 'avocado'} required />
              <Field label="Harvest date" name="harvest_date" type="date" defaultValue={new Date().toISOString().slice(0, 10)} required />
              <Field label="Quantity kg" name="total_quantity_kg" type="number" step="0.1" />
              <Field label="Quality" name="quality_grade" placeholder="Grade A" />
            </FormGrid>
            <div className="border border-slate-100 rounded-xl p-3 max-h-56 overflow-y-auto">
              <p className="text-xs font-bold uppercase text-slate-400 mb-3">Contributing trees</p>
              <div className="grid md:grid-cols-2 gap-2">
                {filteredTrees.slice(0, 80).map(tree => (
                  <label key={tree.id} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 text-sm">
                    <input type="checkbox" name={`tree_${tree.id}`} className="rounded border-slate-300" />
                    <span className="font-medium text-slate-700">{tree.tree_code}</span>
                    <span className="text-slate-400 capitalize">{tree.crop_type}</span>
                  </label>
                ))}
              </div>
            </div>
            <TextArea label="Notes" name="notes" />
            <ModalActions loading={submitting} submitLabel="Create lot" onCancel={() => setShowLotForm(false)} />
          </form>
        </Modal>
      )}

      {/* Tree Grid Builder Drawer */}
      {gridBuilderField && (
        <TreeGridBuilderDrawer
          field={gridBuilderField}
          onClose={() => setGridBuilderField(null)}
          onComplete={() => {
            fetchData();
            setActiveTab('map');
          }}
        />
      )}
    </div>
  );
};

const SidePanel: React.FC<{
  tree: TreeRecord;
  details: any;
  onClose: () => void;
  onObserve: () => void;
  onCare: () => void;
  farmers: FarmerOption[];
  agronomists: StaffOption[];
  cropOptions: string[];
  loading: boolean;
  onAssign: (event: React.FormEvent<HTMLFormElement>) => void;
  onVerify: (type: 'observation' | 'care-event', id: number, status: string, reason?: string) => void;
  verifying: boolean;
}> = ({ tree, details, onClose, onObserve, onCare, farmers, agronomists, cropOptions, loading, onAssign, onVerify, verifying }) => {
  type PassportTab = 'overview' | 'observations' | 'care' | 'issues' | 'lots' | 'evidence';
  const [tab, setTab] = useState<PassportTab>('overview');
  const [detailItem, setDetailItem] = useState<any>(null);
  const [detailType, setDetailType] = useState<'observation' | 'care' | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState<number | null>(null);

  const treePhoto = tree.latest_issue_photo || tree.latest_tree_photo || details?.tree?.latest_issue_photo || details?.tree?.latest_tree_photo;
  const issues = details?.issues || [];
  const observations = details?.observations || [];
  const careEvents = details?.careEvents || [];
  const harvestLots = details?.harvestLots || [];
  const evidence = details?.evidence || [];
  const summary = details?.summary || {};
  const hasReports = Number(tree.open_issue_count || details?.tree?.open_issue_count || 0) > 0 || issues.length > 0;

  const tabs: { key: PassportTab; label: string; count?: number }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'observations', label: 'Observations', count: observations.length },
    { key: 'care', label: 'Care Events', count: careEvents.length },
    { key: 'issues', label: 'Issues', count: issues.length },
    { key: 'lots', label: 'Harvest', count: harvestLots.length },
    { key: 'evidence', label: 'Evidence', count: evidence.length },
  ];

  const openDetail = (item: any, type: 'observation' | 'care') => {
    setDetailItem(item);
    setDetailType(type);
    setShowRejectInput(null);
    setRejectReason('');
  };

  const handleVerify = (type: 'observation' | 'care-event', id: number, status: string) => {
    if (status === 'rejected' && showRejectInput !== id) {
      setShowRejectInput(id);
      return;
    }
    onVerify(type, id, status, rejectReason);
    setShowRejectInput(null);
    setRejectReason('');
  };

  const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
  const fmtDateTime = (d: string) => d ? new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
  <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30">
    <div className="w-full max-w-xl bg-white h-full shadow-2xl flex flex-col">
      {/* Header */}
      <div className="p-5 border-b border-slate-100 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          {treePhoto ? (
            <img src={treePhoto} alt={tree.tree_code} className="h-12 w-12 rounded-xl object-cover" />
          ) : (
            <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-700 font-black text-lg">
              {tree.tree_code?.charAt(0) || '?'}
            </div>
          )}
          <div>
            <p className={`text-[11px] uppercase font-bold ${hasReports ? 'text-red-600' : 'text-emerald-600'}`}>{tree.crop_type}{tree.variety ? ` · ${tree.variety}` : ''}</p>
            <h2 className="text-lg font-black text-slate-900">{tree.tree_code}</h2>
            <p className="text-[11px] text-slate-400">{tree.farmer_name || 'No farmer'} · {tree.plot_code || tree.plot_name || 'No field'}</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100">
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Tab bar */}
      <div className="border-b border-slate-100 px-5 flex gap-1 overflow-x-auto shrink-0">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-2.5 text-xs font-semibold whitespace-nowrap border-b-2 transition ${
              tab === t.key ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}{t.count !== undefined ? ` (${t.count})` : ''}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {tab === 'overview' && (
          <>
            {treePhoto ? (
              <img src={treePhoto} alt={tree.tree_code} className="h-48 w-full rounded-2xl object-cover shadow-sm" />
            ) : (
              <div className="flex h-36 w-full items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50">
                <div className="text-center">
                  <div className="text-3xl">♣</div>
                  <p className="mt-2 text-sm font-bold text-slate-500">No tree image yet</p>
                </div>
              </div>
            )}
            {hasReports && (
              <div className="rounded-2xl border border-red-100 bg-red-50 p-4">
                <p className="text-sm font-black text-red-700">Reported tree</p>
                <p className="mt-1 text-sm leading-6 text-red-600">This tree has an open farmer or field issue. Review before harvest certification.</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              {[
                ['Farmer', tree.farmer_name || 'Not linked'],
                ['Agronomist', tree.assigned_agronomist_name || 'Not assigned'],
                ['Field', tree.plot_code || tree.plot_name || 'Not linked'],
                ['Health', tree.health_status?.replace('_', ' ') || 'Good'],
                ['Status', tree.tree_status?.replace('_', ' ') || 'Registered'],
                ['GPS', tree.latitude && tree.longitude ? `${Number(tree.latitude).toFixed(5)}, ${Number(tree.longitude).toFixed(5)}` : 'Missing'],
              ].map(([label, value]) => (
                <div key={label} className="bg-slate-50 rounded-xl p-3">
                  <p className="text-[11px] text-slate-400 font-bold uppercase">{label}</p>
                  <p className="mt-1 text-sm font-semibold text-slate-800 capitalize">{String(value)}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[
                ['Observations', summary.observation_count || observations.length],
                ['Care Events', summary.care_event_count || careEvents.length],
                ['Open Issues', summary.open_issue_count || issues.filter((i: any) => i.status !== 'Closed').length],
              ].map(([label, value]) => (
                <div key={String(label)} className="bg-emerald-50 rounded-xl p-3 text-center">
                  <p className="text-xl font-black text-emerald-800">{Number(value)}</p>
                  <p className="text-[11px] font-bold uppercase text-emerald-600">{String(label)}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={onObserve} className="flex-1 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700">Add observation</button>
              <button onClick={onCare} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50">Add care event</button>
            </div>
            <div className="flex gap-2">
              <a href={traceabilityExportUrl(tree.id, 'csv')} target="_blank" rel="noreferrer" className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 text-center">Export CSV</a>
              <a href={traceabilityExportUrl(tree.id, 'json')} target="_blank" rel="noreferrer" className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 text-center">Export JSON</a>
              <a href={traceabilityExportUrl(tree.id, 'pdf')} target="_blank" rel="noreferrer" className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 text-center">PDF</a>
            </div>
            <form onSubmit={onAssign} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
              <div className="mb-3">
                <h3 className="text-sm font-black text-slate-900">Assignment</h3>
                <p className="mt-1 text-xs text-slate-500">Link this tree to the responsible farmer, crop, and agronomist.</p>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="block">
                  <span className="text-xs font-bold uppercase text-slate-500">Farmer</span>
                  <select name="farmer_id" defaultValue={tree.farmer_id || ''} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm">
                    <option value="">Not assigned</option>
                    {farmers.map(farmer => <option key={farmer.id} value={farmer.id}>{personName(farmer)}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs font-bold uppercase text-slate-500">Agronomist</span>
                  <select name="assigned_agronomist_id" defaultValue={tree.assigned_agronomist_id || ''} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm">
                    <option value="">Not assigned</option>
                    {agronomists.map(user => <option key={user.id} value={user.id}>{personName(user)}</option>)}
                  </select>
                </label>
                <SelectField label="Crop type" name="crop_type" defaultValue={tree.crop_type || 'avocado'} options={cropOptions.map(crop => ({ value: crop, label: labelize(crop) }))} />
              </div>
              <div className="mt-3 flex justify-end">
                <button disabled={loading} className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-60">
                  {loading ? 'Saving...' : 'Save assignment'}
                </button>
              </div>
            </form>
          </>
        )}

        {tab === 'observations' && (
          <>
            {observations.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">No observations yet.</p>
            ) : observations.map((obs: any) => (
              <div key={obs.id} className="border border-slate-100 rounded-xl p-4 hover:border-emerald-200 transition">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <CalendarDaysIcon className="w-4 h-4" />
                    {fmtDate(obs.observation_date)}
                    {obs.observation_review_status && obs.observation_review_status !== 'pending' && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${obs.observation_review_status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {obs.observation_review_status}
                      </span>
                    )}
                  </div>
                  <button onClick={() => openDetail(obs, 'observation')} className="text-xs font-semibold text-emerald-600 hover:text-emerald-800">Details →</button>
                </div>
                <p className="mt-2 text-sm font-medium text-slate-700 capitalize">{obs.health_status?.replace('_', ' ') || 'No health status'}</p>
                {obs.recommendation && <p className="mt-1 text-xs text-slate-500 line-clamp-2">{obs.recommendation}</p>}
                {obs.photo_urls && (Array.isArray(obs.photo_urls) ? obs.photo_urls.length : JSON.parse(obs.photo_urls || '[]').length) > 0 && (
                  <div className="flex gap-1 mt-2">
                    {(Array.isArray(obs.photo_urls) ? obs.photo_urls : JSON.parse(obs.photo_urls || '[]')).slice(0, 3).map((url: string, i: number) => (
                      <img key={i} src={url} alt="" className="h-12 w-12 rounded-lg object-cover" />
                    ))}
                  </div>
                )}
                {obs.observation_review_status === 'pending' && (
                  <div className="mt-3 flex gap-2">
                    <button
                      disabled={verifying}
                      onClick={() => handleVerify('observation', obs.id, 'approved')}
                      className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 disabled:opacity-50"
                    >
                      Approve
                    </button>
                    <button
                      disabled={verifying}
                      onClick={() => handleVerify('observation', obs.id, 'rejected')}
                      className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-xs font-bold hover:bg-red-50 disabled:opacity-50"
                    >
                      Reject
                    </button>
                    {showRejectInput === obs.id && (
                      <input
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Rejection reason..."
                        className="flex-1 px-2 py-1 rounded-lg border border-red-200 text-xs"
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {tab === 'care' && (
          <>
            {careEvents.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">No care events yet.</p>
            ) : careEvents.map((evt: any) => (
              <div key={evt.id} className="border border-slate-100 rounded-xl p-4 hover:border-emerald-200 transition">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <CalendarDaysIcon className="w-4 h-4" />
                    {fmtDate(evt.event_date)}
                    {evt.verification_status && evt.verification_status !== 'pending' && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${evt.verification_status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {evt.verification_status}
                      </span>
                    )}
                  </div>
                  <button onClick={() => openDetail(evt, 'care')} className="text-xs font-semibold text-emerald-600 hover:text-emerald-800">Details →</button>
                </div>
                <p className="mt-2 text-sm font-medium text-slate-700 capitalize">{evt.event_type?.replace(/_/g, ' ') || 'Care event'}</p>
                {evt.input_name && <p className="mt-1 text-xs text-slate-500">Input: {evt.input_name}{evt.input_quantity ? ` · ${evt.input_quantity} ${evt.input_unit || ''}` : ''}</p>}
                {evt.notes && <p className="mt-1 text-xs text-slate-500 line-clamp-2">{evt.notes}</p>}
                {evt.photo_urls && (Array.isArray(evt.photo_urls) ? evt.photo_urls.length : JSON.parse(evt.photo_urls || '[]').length) > 0 && (
                  <div className="flex gap-1 mt-2">
                    {(Array.isArray(evt.photo_urls) ? evt.photo_urls : JSON.parse(evt.photo_urls || '[]')).slice(0, 3).map((url: string, i: number) => (
                      <img key={i} src={url} alt="" className="h-12 w-12 rounded-lg object-cover" />
                    ))}
                  </div>
                )}
                {evt.verification_status === 'pending' && (
                  <div className="mt-3 flex gap-2">
                    <button
                      disabled={verifying}
                      onClick={() => handleVerify('care-event', evt.id, 'approved')}
                      className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 disabled:opacity-50"
                    >
                      Approve
                    </button>
                    <button
                      disabled={verifying}
                      onClick={() => handleVerify('care-event', evt.id, 'rejected')}
                      className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-xs font-bold hover:bg-red-50 disabled:opacity-50"
                    >
                      Reject
                    </button>
                    {showRejectInput === evt.id && (
                      <input
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Rejection reason..."
                        className="flex-1 px-2 py-1 rounded-lg border border-red-200 text-xs"
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {tab === 'issues' && (
          <>
            {issues.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">No reported issues.</p>
            ) : issues.map((issue: any) => (
              <div key={issue.id} className="border border-slate-100 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <CalendarDaysIcon className="w-4 h-4" />
                    {fmtDate(issue.created_at)}
                    {issue.urgency && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                        issue.urgency === 'High' || issue.urgency === 'high' ? 'bg-red-100 text-red-700' :
                        issue.urgency === 'Medium' || issue.urgency === 'medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {issue.urgency}
                      </span>
                    )}
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${
                    issue.status === 'Closed' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'
                  }`}>{issue.status || 'Open'}</span>
                </div>
                <p className="mt-2 text-sm font-medium text-slate-700">{issue.title || issue.category || 'Issue'}</p>
                {issue.description && <p className="mt-1 text-xs text-slate-500 line-clamp-2">{issue.description}</p>}
                {issue.photo_urls && issue.photo_urls.length > 0 && (
                  <div className="flex gap-1 mt-2">
                    {issue.photo_urls.slice(0, 3).map((url: string, i: number) => (
                      <img key={i} src={url} alt="" className="h-12 w-12 rounded-lg object-cover" />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {tab === 'lots' && (
          <>
            {harvestLots.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">No harvest lots linked.</p>
            ) : harvestLots.map((lot: any) => (
              <div key={lot.id} className="border border-slate-100 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <ClipboardDocumentCheckIcon className="w-5 h-5 text-emerald-600" />
                  <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${statusColor(lot.traceability_status || 'pending')}`}>
                    {lot.traceability_status || 'pending'}
                  </span>
                </div>
                <p className="mt-2 font-bold text-slate-900">{lot.lot_code}</p>
                <p className="text-sm text-slate-500 capitalize">{lot.crop_type} · {fmt(lot.total_quantity_kg)} kg · {fmt(lot.quantity_kg || 0)} kg this tree</p>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <span className="bg-slate-50 rounded-lg p-2">Grade: {lot.quality_grade || '—'}</span>
                  <span className="bg-slate-50 rounded-lg p-2">Date: {fmtDate(lot.harvest_date)}</span>
                </div>
              </div>
            ))}
          </>
        )}

        {tab === 'evidence' && (
          <>
            {evidence.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">No evidence photos yet.</p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {evidence.map((ev: any, i: number) => (
                  <a key={i} href={ev.url} target="_blank" rel="noreferrer" className="block">
                    <img src={ev.url} alt={ev.source || ''} className="aspect-square w-full rounded-xl object-cover hover:ring-2 hover:ring-emerald-400 transition" />
                    <p className="text-[10px] text-slate-400 mt-1 truncate">{ev.source || 'photo'} · {fmtDate(ev.date)}</p>
                  </a>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail modal */}
      {detailItem && detailType && (
        <div className="fixed inset-0 z-[60] bg-slate-900/50 flex items-center justify-center p-4" onClick={() => setDetailItem(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-base font-black text-slate-900">{detailType === 'observation' ? 'Observation Detail' : 'Care Event Detail'}</h3>
              <button onClick={() => setDetailItem(null)} className="p-2 rounded-lg hover:bg-slate-100"><XMarkIcon className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              {detailType === 'observation' ? (
                <>
                  <DetailRow label="Date" value={fmtDateTime(detailItem.observation_date)} />
                  <DetailRow label="Health Status" value={detailItem.health_status?.replace('_', ' ')} />
                  <DetailRow label="Canopy Condition" value={detailItem.canopy_condition} />
                  <DetailRow label="Canopy Vigor" value={detailItem.canopy_vigor} />
                  <DetailRow label="Leaf Color" value={detailItem.leaf_color} />
                  <DetailRow label="Fruit Size (mm)" value={detailItem.fruit_size_diameter} />
                  <DetailRow label="Pest Signs" value={detailItem.pest_signs} />
                  <DetailRow label="Pest Type" value={detailItem.pest_type} />
                  <DetailRow label="Disease Signs" value={detailItem.disease_signs} />
                  <DetailRow label="Disease Type" value={detailItem.disease_type} />
                  <DetailRow label="Severity Score" value={detailItem.severity_score} />
                  <DetailRow label="Weed Pressure" value={detailItem.weed_pressure} />
                  <DetailRow label="Erosion Status" value={detailItem.erosion_status} />
                  <DetailRow label="Drainage Status" value={detailItem.drainage_status} />
                  <DetailRow label="Soil Condition" value={detailItem.soil_condition} />
                  <DetailRow label="Estimated Yield (kg)" value={detailItem.estimated_yield_kg} />
                  <DetailRow label="GPS" value={detailItem.latitude && detailItem.longitude ? `${Number(detailItem.latitude).toFixed(6)}, ${Number(detailItem.longitude).toFixed(6)}` : null} />
                  <DetailRow label="GPS Accuracy (m)" value={detailItem.gps_accuracy_m} />
                  <DetailRow label="Urgency" value={detailItem.urgency} />
                  <DetailRow label="Follow-up Required" value={detailItem.follow_up_required ? 'Yes' : null} />
                  <DetailRow label="Follow-up Date" value={detailItem.follow_up_date} />
                  <DetailRow label="Recommendation" value={detailItem.recommendation} />
                  <DetailRow label="Reviewed By" value={detailItem.verified_by || detailItem.observation_review_status} />
                  <DetailRow label="Rejection Reason" value={detailItem.rejection_reason} />
                  {detailItem.photo_urls && (Array.isArray(detailItem.photo_urls) ? detailItem.photo_urls : JSON.parse(detailItem.photo_urls || '[]')).length > 0 && (
                    <div>
                      <p className="text-[11px] font-bold uppercase text-slate-500 mb-2">Photos</p>
                      <div className="flex gap-2 flex-wrap">
                        {(Array.isArray(detailItem.photo_urls) ? detailItem.photo_urls : JSON.parse(detailItem.photo_urls || '[]')).map((url: string, i: number) => (
                          <a key={i} href={url} target="_blank" rel="noreferrer"><img src={url} alt="" className="h-20 w-20 rounded-xl object-cover" /></a>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <DetailRow label="Date" value={fmtDateTime(detailItem.event_date)} />
                  <DetailRow label="Event Type" value={detailItem.event_type?.replace(/_/g, ' ')} />
                  <DetailRow label="Category" value={detailItem.activity_category} />
                  <DetailRow label="Input Name" value={detailItem.input_name} />
                  <DetailRow label="Active Ingredient" value={detailItem.active_ingredient} />
                  <DetailRow label="Supplier" value={detailItem.supplier} />
                  <DetailRow label="Batch Number" value={detailItem.input_batch_number} />
                  <DetailRow label="Quantity" value={detailItem.input_quantity ? `${detailItem.input_quantity} ${detailItem.input_unit || ''}` : null} />
                  <DetailRow label="Dilution Rate" value={detailItem.dilution_rate} />
                  <DetailRow label="Qty per Tree" value={detailItem.quantity_per_tree} />
                  <DetailRow label="Qty per Plot" value={detailItem.quantity_per_plot} />
                  <DetailRow label="Worker" value={detailItem.worker_name || detailItem.performed_by} />
                  <DetailRow label="Labor Hours" value={detailItem.labor_hours} />
                  <DetailRow label="Labor Cost (RWF)" value={detailItem.labor_cost} />
                  <DetailRow label="Total Cost (RWF)" value={detailItem.cost_rwf || detailItem.total_cost} />
                  <DetailRow label="Pre-Harvest Interval" value={detailItem.pre_harvest_interval_days ? `${detailItem.pre_harvest_interval_days} days` : null} />
                  <DetailRow label="Re-entry Interval" value={detailItem.re_entry_interval_hours ? `${detailItem.re_entry_interval_hours} hours` : null} />
                  <DetailRow label="Protective Equipment" value={detailItem.protective_equipment} />
                  <DetailRow label="Result After Care" value={detailItem.result_after_care} />
                  <DetailRow label="Next Action Date" value={detailItem.next_action_date} />
                  <DetailRow label="GPS" value={detailItem.latitude && detailItem.longitude ? `${Number(detailItem.latitude).toFixed(6)}, ${Number(detailItem.longitude).toFixed(6)}` : null} />
                  <DetailRow label="GPS Accuracy (m)" value={detailItem.gps_accuracy_m} />
                  <DetailRow label="Notes" value={detailItem.notes} />
                  <DetailRow label="Verified By" value={detailItem.verified_by || detailItem.verification_status} />
                  <DetailRow label="Rejection Reason" value={detailItem.rejection_reason} />
                  {detailItem.photo_urls && (Array.isArray(detailItem.photo_urls) ? detailItem.photo_urls : JSON.parse(detailItem.photo_urls || '[]')).length > 0 && (
                    <div>
                      <p className="text-[11px] font-bold uppercase text-slate-500 mb-2">Photos</p>
                      <div className="flex gap-2 flex-wrap">
                        {(Array.isArray(detailItem.photo_urls) ? detailItem.photo_urls : JSON.parse(detailItem.photo_urls || '[]')).map((url: string, i: number) => (
                          <a key={i} href={url} target="_blank" rel="noreferrer"><img src={url} alt="" className="h-20 w-20 rounded-xl object-cover" /></a>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
  );
};

const DetailRow: React.FC<{ label: string; value: any }> = ({ label, value }) => {
  if (!value && value !== 0) return null;
  return (
    <div className="flex justify-between items-start py-1.5 border-b border-slate-50 last:border-0">
      <span className="text-[11px] font-bold uppercase text-slate-400">{label}</span>
      <span className="text-sm font-medium text-slate-700 text-right max-w-[60%]">{String(value)}</span>
    </div>
  );
};

const EditFieldModal: React.FC<{
  field: FieldPlot;
  farmers: FarmerOption[];
  agronomists: StaffOption[];
  loading: boolean;
  onClose: () => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}> = ({ field, farmers, agronomists, loading, onClose, onSubmit }) => {
  const initialFarmerIds = Array.isArray(field.farmers) && field.farmers.length
    ? field.farmers.map(member => String(member.id))
    : (field.farmer_id ? [String(field.farmer_id)] : []);
  const [selectedFarmerIds, setSelectedFarmerIds] = useState<string[]>(initialFarmerIds);
  return (
  <Modal title={`Edit batch ${field.plot_code}`} onClose={onClose} wide>
    <form onSubmit={onSubmit} className="space-y-5">
      <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
        <p className="text-sm font-black text-slate-900">{field.name || field.plot_code}</p>
        <p className="mt-1 text-xs text-slate-500">
          Update planning values and the assigned farmer team. Existing trees stay registered.
        </p>
      </div>
      <FormGrid>
        <Field label="Field name" name="name" defaultValue={field.name || ''} />
        <Field label="Crop type" name="crop_type" defaultValue={field.crop_type || 'avocado'} />
        <Field label="Expected trees" name="expected_tree_count" type="number" defaultValue={field.expected_tree_count || 0} />
        <Field label="Rows" name="grid_rows" type="number" defaultValue={field.grid_rows || ''} />
        <Field label="Columns" name="grid_columns" type="number" defaultValue={field.grid_columns || ''} />
        <Field label="Area hectares" name="area_hectares" type="number" step="0.01" defaultValue={field.area_hectares || ''} />
        <Field label="Row spacing m" name="row_spacing_m" type="number" step="0.1" />
        <Field label="Tree spacing m" name="tree_spacing_m" type="number" step="0.1" />
        <label className="block">
          <span className="text-xs font-bold uppercase text-slate-500">Assigned farmers</span>
          <select
            name="farmer_ids"
            multiple
            value={selectedFarmerIds}
            onChange={(event) => setSelectedFarmerIds(Array.from(event.target.selectedOptions).map((option) => option.value))}
            className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          >
            {farmers.map(farmer => <option key={farmer.id} value={farmer.id}>{personName(farmer)}</option>)}
          </select>
          <p className="mt-1 text-xs font-semibold text-slate-400">Hold Cmd/Ctrl to select multiple farmers. Saving replaces this field's farmer team.</p>
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase text-slate-500">Primary contact farmer</span>
          <select name="primary_farmer_id" defaultValue={String(field.farmer_id || '')} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
            <option value="">Defaults to first selected farmer</option>
            {selectedFarmerIds.map(id => {
              const farmer = farmers.find(f => String(f.id) === id);
              return <option key={id} value={id}>{farmer ? personName(farmer) : `Farmer ${id}`}</option>;
            })}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase text-slate-500">Assigned agronomist</span>
          <select name="assigned_agronomist_id" defaultValue={field.assigned_agronomist_id || ''} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
            <option value="">Not assigned</option>
            {agronomists.map(user => <option key={user.id} value={user.id}>{personName(user)}</option>)}
          </select>
        </label>
      </FormGrid>
      <ModalActions loading={loading} submitLabel="Save batch" onCancel={onClose} />
    </form>
  </Modal>
  );
};

const AddTreeModal: React.FC<{
  farmers: FarmerOption[];
  mapFarmers: FarmerOption[];
  fields: FieldPlot[];
  agronomists: StaffOption[];
  cropOptions: string[];
  loading: boolean;
  onClose: () => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}> = ({ fields, agronomists, cropOptions, loading, onClose, onSubmit }) => {
  const [selectedFieldId, setSelectedFieldId] = useState('');
  const [selectedPoint, setSelectedPoint] = useState<{ lat: number; lng: number } | null>(null);
  const selectedField = fields.find(field => Number(field.id) === Number(selectedFieldId));
  const fieldBoundary = parseMapBoundary(selectedField?.boundary_coordinates);
  const center = fieldBoundary.length >= 3
    ? {
      lat: fieldBoundary.reduce((sum, point) => sum + point.lat, 0) / fieldBoundary.length,
      lng: fieldBoundary.reduce((sum, point) => sum + point.lng, 0) / fieldBoundary.length,
    }
    : RWANDA_DEFAULTS.center;
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (!selectedPoint) {
      event.preventDefault();
      alert('Please click the tree location on the map before saving.');
      return;
    }
    onSubmit(event);
  };

  return (
    <Modal title="Add tree" onClose={onClose} wide>
      <form onSubmit={handleSubmit} className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="space-y-4">
          <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
            <div className="flex items-center gap-2 text-sm font-bold text-emerald-900">
              <MapPinIcon className="h-5 w-5" />
              Map-based tree registration
            </div>
            <p className="mt-2 text-sm leading-6 text-emerald-800">
              Choose a mapped farm field, then click the exact tree position inside that boundary.
            </p>
          </div>

          <FormGrid>
            <SelectField label="Crop type" name="crop_type" required defaultValue={selectedField?.crop_type || 'avocado'} options={cropOptions.map(crop => ({ value: crop, label: labelize(crop) }))} />
            <Field label="Variety" name="variety" placeholder="Hass, Fuerte..." />
            <label className="block">
              <span className="text-xs font-bold uppercase text-slate-500">Farm field</span>
              <select
                name="plot_id"
                required
                value={selectedFieldId}
                onChange={(event) => {
                  setSelectedFieldId(event.target.value);
                  setSelectedPoint(null);
                }}
                className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              >
                <option value="">Select mapped farm field</option>
                {fields.map(field => <option key={field.id} value={field.id}>{field.plot_code} - {field.name || field.farmer_name || 'Mapped field'}</option>)}
              </select>
            </label>
            <input type="hidden" name="farmer_id" value={selectedField?.farmer_id || ''} readOnly />
            <Field label="Age years" name="estimated_age_years" type="number" step="0.1" />
            <SelectField label="Health" name="health_status" options={healthOptions.map(v => ({ value: v, label: labelize(v) }))} />
            <label className="block">
              <span className="text-xs font-bold uppercase text-slate-500">Assigned agronomist</span>
              <select name="assigned_agronomist_id" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
                <option value="">Not assigned</option>
                {agronomists.map(user => <option key={user.id} value={user.id}>{personName(user)}</option>)}
              </select>
            </label>
          </FormGrid>

          <input type="hidden" name="latitude" value={selectedPoint?.lat ?? ''} readOnly />
          <input type="hidden" name="longitude" value={selectedPoint?.lng ?? ''} readOnly />

          <div className={`rounded-xl border p-3 text-sm ${selectedPoint ? 'border-emerald-100 bg-emerald-50 text-emerald-800' : 'border-amber-100 bg-amber-50 text-amber-800'}`}>
            {selectedPoint
              ? `Selected: ${selectedPoint.lat.toFixed(6)}, ${selectedPoint.lng.toFixed(6)}`
              : 'Click the map to set this tree location before saving.'}
          </div>

          <TextArea label="Notes" name="notes" />
          <ModalActions loading={loading} submitLabel="Create tree" onCancel={onClose} />
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-100 bg-slate-50">
          <div className="border-b border-slate-100 bg-white px-4 py-3">
            <h3 className="text-sm font-black text-slate-900">Tree location</h3>
            <p className="text-xs text-slate-500">Map is focused on the selected farm field.</p>
          </div>
          <div className="h-[520px]">
            <TreeLocationPicker
              center={center}
              farmerBoundary={fieldBoundary}
              selectedPoint={selectedPoint}
              onSelect={setSelectedPoint}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};

const TreeLocationPicker: React.FC<{
  center: { lat: number; lng: number };
  farmerBoundary: Array<{ lat: number; lng: number }>;
  selectedPoint: { lat: number; lng: number } | null;
  onSelect: (point: { lat: number; lng: number }) => void;
}> = ({ center, farmerBoundary, selectedPoint, onSelect }) => {
  const handleMapClick = React.useCallback((lat: number, lng: number) => {
    const point = { lat, lng };
    if (farmerBoundary.length < 3) {
      window.alert('Select a mapped farm field before placing a tree.');
      return;
    }
    if (!pointInsideBoundary(point, farmerBoundary)) {
      window.alert('Choose a point inside the selected farm field boundary.');
      return;
    }
    onSelect(point);
  }, [farmerBoundary, onSelect]);

  const polygons = farmerBoundary.length >= 3
    ? [{ id: 'boundary', coordinates: farmerBoundary.map(p => [p.lat, p.lng] as [number, number]), color: '#059669', fillColor: '#10b981', fillOpacity: 0.24 }]
    : [];

  const markers = selectedPoint
    ? [{ id: 'selected', position: [selectedPoint.lat, selectedPoint.lng] as [number, number], color: '#16a34a' }]
    : [];

  return (
    <MapboxMiniMap
      center={[center.lat, center.lng]}
      zoom={farmerBoundary.length >= 3 ? 18 : 16}
      basemap="satellite"
      style={{ height: '100%', width: '100%' }}
      scrollWheelZoom
      cursor="crosshair"
      polygons={polygons}
      markers={markers}
      onMapClick={handleMapClick}
      fitBounds={farmerBoundary.length >= 3}
    />
  );
};

const EmptyState: React.FC<{
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  text: string;
  action?: string;
  onAction?: () => void;
}> = ({ icon: Icon, title, text, action, onAction }) => (
  <div className="lg:col-span-3 flex flex-col items-center justify-center px-6 py-14 text-center">
    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50">
      <Icon className="h-6 w-6 text-emerald-700" />
    </div>
    <h3 className="mt-4 text-base font-black text-slate-900">{title}</h3>
    <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">{text}</p>
    {action && onAction && (
      <button
        onClick={onAction}
        className="mt-5 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-slate-800"
      >
        {action}
      </button>
    )}
  </div>
);

const FieldGridModal: React.FC<{
  farmers: FarmerOption[];
  mapFarmers: FarmerOption[];
  plots: FieldPlot[];
  agronomists: StaffOption[];
  cropOptions: string[];
  loading: boolean;
  onClose: () => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}> = ({ farmers, mapFarmers, plots, agronomists, cropOptions, loading, onClose, onSubmit }) => {
  const [selectedFarmerIds, setSelectedFarmerIds] = useState<string[]>([]);
  const [targetMode, setTargetMode] = useState<'existing' | 'new'>('existing');
  const [selectedPlotId, setSelectedPlotId] = useState('');
  const [treeCount, setTreeCount] = useState(100);
  const [rows, setRows] = useState(10);
  const [columns, setColumns] = useState(10);
  const capacity = Math.max(rows * columns, 0);
  const overflow = treeCount > capacity;
  const previewCells = Array.from({ length: Math.min(capacity || 0, 80) }, (_, index) => index < treeCount);
  const selectedPlot = plots.find(plot => String(plot.id) === selectedPlotId);
  const isExisting = targetMode === 'existing';
  const previewFarmerId = selectedFarmerIds[0] || '';
  const selectedFarmer = farmers.find(farmer => Number(farmer.id) === Number(previewFarmerId));
  const selectedMapFarmer = mapFarmers.find(farmer => Number(farmer.id) === Number(previewFarmerId));
  const farmerBoundary = parseMapBoundary(selectedMapFarmer?.plot_boundary_coordinates || selectedFarmer?.plot_boundary_coordinates || selectedFarmer?.plot_boundary);
  const plotBoundary = parseMapBoundary(selectedPlot?.boundary_coordinates);
  const boundaryValid = isExisting ? Boolean(selectedPlot) && plotBoundary.length >= 3 : farmerBoundary.length >= 3;
  const selectedPlotNeedsBoundary = isExisting && Boolean(selectedPlot) && plotBoundary.length < 3;
  const effectiveBoundary = isExisting ? plotBoundary : farmerBoundary;
  const mapCenter = effectiveBoundary.length >= 3
    ? {
      lat: effectiveBoundary.reduce((sum: number, point: { lat: number; lng: number }) => sum + point.lat, 0) / effectiveBoundary.length,
      lng: effectiveBoundary.reduce((sum: number, point: { lat: number; lng: number }) => sum + point.lng, 0) / effectiveBoundary.length,
    }
    : RWANDA_DEFAULTS.center;
  const previewCohorts: any[] = [];
  const autoHectares = computeBoundaryAreaHectares(effectiveBoundary);
  const existingTrees = Number(selectedPlot?.mapped_tree_count || 0);
  const alreadyAligned = existingTrees > 0;
  const previewFarmers = effectiveBoundary.length >= 3 ? [{
    id: isExisting ? -Number(selectedPlot!.id) : Number(selectedMapFarmer?.id || previewFarmerId || 0),
    name: isExisting
      ? (selectedPlot?.name || selectedPlot?.plot_code || 'Selected field')
      : (selectedMapFarmer?.name || selectedMapFarmer?.full_name || selectedFarmer?.full_name || `Farmer ${previewFarmerId}`),
    household_type: 'farmer',
    plot_boundary_coordinates: effectiveBoundary,
    cohort_name: '',
    cohort_id: 0,
    plot_area_hectares: Number((isExisting ? selectedPlot?.area_hectares : selectedMapFarmer?.plot_area_hectares) || 0),
  }] : [];

  const autoGrid = () => {
    const nextColumns = Math.ceil(Math.sqrt(treeCount || 1));
    const nextRows = Math.ceil((treeCount || 1) / nextColumns);
    setRows(nextRows);
    setColumns(nextColumns);
  };

  return (
    <Modal title={isExisting ? 'Insert trees into mapped field' : 'Create mapped farm field'} onClose={onClose} wide>
      <form onSubmit={onSubmit} className="grid gap-5 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="space-y-4">
          <input type="hidden" name="target_plot_id" value={isExisting ? selectedPlotId : ''} readOnly />

          <div className={`rounded-2xl border p-4 ${isExisting && selectedPlot ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}>
            <p className="flex items-center gap-2 text-sm font-black text-slate-900">
              <MapPinIcon className="h-5 w-5 text-emerald-700" />
              1 · {isExisting ? 'Select the farm field' : 'New field details'}
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setTargetMode('existing')}
                className={`rounded-lg px-2 py-2 text-xs font-black ${isExisting ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                Existing field
              </button>
              <button
                type="button"
                onClick={() => setTargetMode('new')}
                className={`rounded-lg px-2 py-2 text-xs font-black ${!isExisting ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                Create new field
              </button>
            </div>

            {isExisting ? (
              <div className="mt-3 space-y-3">
                <label className="block">
                  <span className="text-xs font-bold uppercase text-slate-500">Mapped field</span>
                  <select
                    value={selectedPlotId}
                    onChange={(event) => setSelectedPlotId(event.target.value)}
                    required
                    className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                  >
                    <option value="">Select a mapped field…</option>
                    {plots.map(plot => (
                      <option key={plot.id} value={plot.id}>
                        {(plot.name || plot.plot_code || `Field ${plot.id}`)} · {labelize(plot.crop_type)} · {Number(plot.mapped_tree_count || 0)}/{Number(plot.expected_tree_count || 0)} trees{Number(plot.area_hectares) > 0 ? ` · ${Number(plot.area_hectares).toFixed(2)} ha` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs font-semibold text-slate-400">Trees are inserted inside this field's existing boundary — nothing is duplicated.</p>
                </label>

                {selectedPlot && (
                  <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                    <div className="rounded-lg bg-white p-2.5 shadow-sm"><p className="font-black text-slate-900">{labelize(selectedPlot.crop_type)}</p><p className="text-slate-500">Crop</p></div>
                    <div className="rounded-lg bg-white p-2.5 shadow-sm"><p className="font-black text-slate-900">{Number(selectedPlot.area_hectares || 0).toFixed(2)} ha</p><p className="text-slate-500">Area</p></div>
                    <div className="rounded-lg bg-white p-2.5 shadow-sm"><p className="font-black text-slate-900">{existingTrees}/{Number(selectedPlot.expected_tree_count || 0)}</p><p className="text-slate-500">Trees aligned</p></div>
                    <div className="rounded-lg bg-white p-2.5 shadow-sm truncate" title={(selectedPlot.farmers || []).map(f => f.name).join(', ')}>
                      <p className="font-black text-slate-900">{selectedPlot.primary_farmer_name || selectedPlot.farmer_name || '—'}{(Number(selectedPlot.farmer_count || 0)) > 1 ? ` +${Number(selectedPlot.farmer_count) - 1}` : ''}</p>
                      <p className="text-slate-500">Farmers</p>
                    </div>
                  </div>
                )}
                {selectedPlotNeedsBoundary && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-xs leading-5 text-amber-800">
                    <p className="font-black">This field exists, but it has no saved GPS boundary.</p>
                    <p className="mt-1 font-semibold">Open Farm Maps, draw and save the field boundary, then return here to generate the tree grid.</p>
                  </div>
                )}
                {alreadyAligned && (
                  <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs font-bold text-amber-800">
                    This field was already aligned ({existingTrees} trees). Aligning runs only once per field to prevent duplicates.
                  </p>
                )}
              </div>
            ) : (
              <FormGrid>
                <Field label="Field name" name="name" placeholder="Huye block A" />
                <SelectField label="Crop type" name="crop_type" required defaultValue="avocado" options={cropOptions.map(crop => ({ value: crop, label: labelize(crop) }))} />
              </FormGrid>
            )}
          </div>

          {!isExisting && (
            <>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="flex items-center gap-2 text-sm font-black text-slate-900">
              <UserGroupIcon className="h-5 w-5 text-emerald-700" />
              2 · Farmers &amp; ownership
            </p>
            <div className="mt-3 space-y-3">
              <label className="block">
              <span className="text-xs font-bold uppercase text-slate-500">Assigned farmers</span>
              <select
                name="farmer_ids"
                required
                multiple
                value={selectedFarmerIds}
                onChange={(event) => setSelectedFarmerIds(Array.from(event.target.selectedOptions).map((option) => option.value))}
                className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              >
                {farmers.map(f => <option key={f.id} value={f.id}>{f.full_name || f.name || `Farmer ${f.id}`}</option>)}
              </select>
              <p className="mt-1 text-xs font-semibold text-slate-400">Hold Cmd/Ctrl to select multiple farmers. ONE mapped field is created and every selected farmer is assigned to it.</p>
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase text-slate-500">Primary contact farmer</span>
                <select
                  name="primary_farmer_id"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                >
                  {selectedFarmerIds.length === 0 && <option value="">Defaults to first selected farmer</option>}
                  {selectedFarmerIds.map(id => {
                    const farmer = farmers.find(f => String(f.id) === id);
                    return <option key={id} value={id}>{farmer?.full_name || farmer?.name || `Farmer ${id}`}</option>;
                  })}
                </select>
                <p className="mt-1 text-xs font-semibold text-slate-400">Shown as the field owner on reports and legacy views.</p>
              </label>
            </div>
          </div>

          <FormGrid>
              <Field label="Variety" name="variety" placeholder="Hass, Fuerte..." />
              <label className="block">
                <span className="text-xs font-bold uppercase text-slate-500">Assigned agronomist</span>
                <select name="assigned_agronomist_id" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
                  <option value="">Not assigned</option>
                  {agronomists.map(user => <option key={user.id} value={user.id}>{personName(user)}</option>)}
                </select>
              </label>
            </FormGrid>
            </>
          )}

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="flex items-center gap-2 text-sm font-black text-slate-900">
              <BeakerIcon className="h-5 w-5 text-emerald-700" />
              {isExisting ? '2 · Trees to insert' : '2 · Tree grid & location'}
            </p>
            <FormGrid>
              <Field label="Expected trees" name="expected_tree_count" type="number" value={treeCount} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTreeCount(Number(event.target.value) || 0)} required />
              <Field label="Rows" name="grid_rows" type="number" value={rows} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setRows(Number(event.target.value) || 0)} required />
              <Field label="Columns" name="grid_columns" type="number" value={columns} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setColumns(Number(event.target.value) || 0)} required />
              <button type="button" onClick={autoGrid} className="mt-5 rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50">
                Auto grid
              </button>
              <Field label="Row spacing m" name="row_spacing_m" type="number" step="0.1" defaultValue="6" />
              <Field label="Tree spacing m" name="tree_spacing_m" type="number" step="0.1" defaultValue="6" />
              {!isExisting && (
                <>
                  <Field label="District" name="district" />
                  <Field label="Sector" name="sector" />
                </>
              )}
            </FormGrid>
          </div>
          <input type="hidden" name="boundary_coordinates" value={effectiveBoundary.length >= 3 ? JSON.stringify(effectiveBoundary) : ''} readOnly />
          <input type="hidden" name="center_lat" value={mapCenter.lat || ''} readOnly />
          <input type="hidden" name="center_lng" value={mapCenter.lng || ''} readOnly />

          <p className={`flex items-center gap-2 rounded-xl border px-3 py-3 text-sm font-bold ${boundaryValid && !overflow ? 'border-emerald-100 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
            <BeakerIcon className="h-5 w-5 shrink-0" />
            {!boundaryValid
              ? (selectedPlotNeedsBoundary
                ? 'This field has no saved boundary. Draw/save it in Farm Maps before inserting trees.'
                : isExisting
                  ? 'Select a mapped field first — trees are inserted inside its existing boundary.'
                  : 'Select a farmer whose land is mapped, so trees get real GPS coordinates.')
              : overflow
                ? `Grid too small: ${rows}×${columns} = ${capacity} cells for ${treeCount} trees. Increase rows/columns or press Auto grid.`
                : `${treeCount > 0 ? `${treeCount} trees` : 'No trees'} will be ${isExisting ? `inserted into ${selectedPlot?.name || selectedPlot?.plot_code || 'the selected field'}` : 'created inside the field boundary'}.`}
          </p>
          <ModalActions
            loading={loading}
            disabled={isExisting && (!selectedPlotId || !boundaryValid || alreadyAligned)}
            submitLabel={isExisting ? (treeCount > 0 ? `Insert ${treeCount} trees` : 'Insert trees') : 'Create mapped field'}
            onCancel={onClose}
          />
        </div>

        <aside className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-black text-slate-900">Land preview</h3>
              <p className="text-xs text-slate-500">
                {treeCount} trees aligned into mapped land
                {autoHectares ? ` · ≈ ${autoHectares.toLocaleString()} ha` : ''}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className={`rounded-full px-2 py-1 text-[11px] font-bold ${overflow || !boundaryValid ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                {!boundaryValid ? 'Pick a field' : overflow ? 'Need space' : 'Ready'}
              </span>
              <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-slate-400 shadow-sm">
                {isExisting ? (selectedPlot ? 'Existing field boundary' : 'No field selected') : farmerBoundary.length >= 3 ? "Farmer's mapped land" : 'No boundary yet'}
              </span>
            </div>
          </div>
          <div className="relative mt-4 h-64 overflow-hidden rounded-xl border border-white bg-white">
            <MapBaseComponent
              farmers={previewFarmers}
              cohorts={previewCohorts}
              warehouses={[]}
              trees={[]}
              center={mapCenter}
              zoom={17}
              baseLayer="satellite"
              sizeToken={`${isExisting ? 'p' : 'f'}-${isExisting ? selectedPlotId : previewFarmerId}-${treeCount}-${effectiveBoundary.length}`}
            />
            {!boundaryValid && (
              <div className="pointer-events-none absolute inset-0 z-[500] flex flex-col items-center justify-center gap-2 bg-slate-900/55 px-6 text-center backdrop-blur-[1px]">
                <MapPinIcon className="h-8 w-8 text-white" />
                <p className="text-sm font-black text-white">{selectedPlotNeedsBoundary ? 'Field boundary missing' : isExisting ? 'Select a mapped field' : 'Select a farmer with mapped land'}</p>
                <p className="max-w-xs text-xs leading-5 text-white/80">
                  {selectedPlotNeedsBoundary
                    ? 'This field must be opened in Farm Maps and saved with a drawn boundary before trees can be placed.'
                    : isExisting
                    ? 'Choose the farm field from step 1 — its boundary appears here and trees are inserted inside it.'
                    : 'Trees need GPS coordinates. Pick a farmer whose land is already mapped.'}
                </p>
              </div>
            )}
          </div>
          <div
            className="mt-4 grid gap-1 rounded-xl bg-white p-3"
            style={{ gridTemplateColumns: `repeat(${Math.min(columns || 1, 12)}, minmax(0, 1fr))` }}
          >
            {previewCells.map((active, index) => (
              <div
                key={index}
                className={`aspect-square rounded ${active ? 'bg-emerald-600' : 'bg-slate-100'} ${index >= 80 ? 'hidden' : ''}`}
              />
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
            <div className="rounded-lg bg-white p-3">
              <p className="font-bold text-slate-900">{rows}</p>
              <p className="text-slate-500">Rows</p>
            </div>
            <div className="rounded-lg bg-white p-3">
              <p className="font-bold text-slate-900">{columns}</p>
              <p className="text-slate-500">Columns</p>
            </div>
          </div>
          <p className="mt-4 text-xs leading-5 text-slate-500">
            Tree coordinates are generated from this farm field boundary. Cohorts remain optional metadata only.
          </p>
        </aside>
      </form>
    </Modal>
  );
};

const Modal: React.FC<{ title: string; onClose: () => void; wide?: boolean; children: React.ReactNode }> = ({ title, onClose, wide, children }) => (
  <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4">
    <div className={`bg-white rounded-2xl shadow-2xl w-full ${wide ? 'max-w-4xl' : 'max-w-2xl'} max-h-[92vh] overflow-hidden flex flex-col`}>
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100">
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
      <div className="p-5 overflow-y-auto">{children}</div>
    </div>
  </div>
);

const FormGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid md:grid-cols-2 gap-4">{children}</div>
);

const Field: React.FC<any> = ({ label, ...props }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase text-slate-500">{label}</span>
    <input {...props} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
  </label>
);

const TextArea: React.FC<any> = ({ label, ...props }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase text-slate-500">{label}</span>
    <textarea {...props} rows={3} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 resize-none" />
  </label>
);

const SelectField: React.FC<{ label: string; name: string; options: Array<{ value: string | number; label: string }>; required?: boolean; defaultValue?: string | number }> = ({ label, name, options, required, defaultValue }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase text-slate-500">{label}</span>
    <select name={name} required={required} defaultValue={defaultValue || ''} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
      <option value="">Select</option>
      {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
    </select>
  </label>
);

const ModalActions: React.FC<{ loading: boolean; submitLabel: string; onCancel: () => void; disabled?: boolean }> = ({ loading, submitLabel, onCancel, disabled }) => (
  <div className="flex justify-end gap-2 pt-2">
    <button type="button" onClick={onCancel} className="px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
    <button disabled={loading || disabled} className="px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60">
      {loading ? 'Saving...' : submitLabel}
    </button>
  </div>
);

export default TraceabilityPage;
